import React, {useCallback} from 'react';
import {
  withSystem,
  withMeta,
  Expressions,
  Plugins,
  ExpressionPath,
} from '@packages/systems/wfdl';
import {
  type ContextualExpression,
  ContextualExpressions,
  stripAll,
} from '@packages/utilities/contextual-expression';
import {
  EElement,
  TEnum,
  TText,
  TTypeConstructor,
} from '@packages/systems/wfdl-ast';
import {qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {
  ExpressionEditor,
  extendExpressionEditorActionAtPath,
} from '@packages/systems/ExpressionEditor-utils';
import {Button, VStack, TabNewIcon} from '@packages/systems/spring';
import {
  LANGUAGE_PATH,
  LINE_NUMBERS_PATH,
  THEME_PATH,
  SUPPORTED_LANGUAGES,
  THEMES,
  CODE_PATH,
  DEFAULT_LANGUAGE,
} from '@packages/systems/core/constants/CodeBlock';
import {CODE_BLOCK_OPEN_EDITOR} from '@packages/systems/designer/actions';
import {
  useDesignerSelector,
  useDispatch,
  useFluxContext,
  type DesignerFlux,
  type DesignerStores,
} from '@packages/systems/designer/flux';
import {selectSelectedElement} from '@packages/utilities/platform-selectors';
import {createIntegratedRootAlias} from '@packages/utilities/integrated-types';

const {getIn} = ContextualExpressions;

const selectContentEditorStore = (state: DesignerStores) =>
  state.ContentEditorStore;

const CodeBlockTypeRender = ({
  expr,
  dispatch,
}: {
  expr: ContextualExpression;
  dispatch: any;
}) => {
  const wfDispatch = useDispatch();
  const flux = useFluxContext<DesignerFlux>();
  const contentEditorStore = useDesignerSelector(selectContentEditorStore);

  const expressionEditorAtPath = useCallback(
    (path: ExpressionPath) => {
      const exprAt = getIn(expr, path);
      if (!exprAt) {
        throw Error(
          `Failed to get expression at path: ${JSON.stringify(path)}`
        );
      }
      const dispatchAt = extendExpressionEditorActionAtPath(dispatch)(path);

      return <ExpressionEditor expression={exprAt} dispatch={dispatchAt} />;
    },
    [expr, dispatch]
  );

  const handleOpenCodeEditor = () => {
    const strippedExpr = stripAll(expr);
    const contentEditorNodeId = contentEditorStore.getIn([
      'selection',
      'nodeId',
    ]) as string | undefined;

    let selectedElement = selectSelectedElement(flux.state);

    if (contentEditorNodeId && contentEditorStore.subtree) {
      selectedElement = Expressions.getElementById(
        EElement(contentEditorStore.subtree),
        contentEditorNodeId
      );
    }

    if (!selectedElement) {
      return;
    }

    const initialCodeExpression = Expressions.getIn(strippedExpr, CODE_PATH);
    const languageExpr = Expressions.getIn(strippedExpr, LANGUAGE_PATH);
    const language = languageExpr
      ? Expressions.getEnum(languageExpr)
      : DEFAULT_LANGUAGE;

    wfDispatch({
      type: CODE_BLOCK_OPEN_EDITOR,
      payload: {
        nodeId: selectedElement.id,
        initialCodeExpression: initialCodeExpression ?? null,
        language: language ?? DEFAULT_LANGUAGE,
      },
    });
  };

  const Language = expressionEditorAtPath(LANGUAGE_PATH);
  const Theme = expressionEditorAtPath(THEME_PATH);
  const LineNumbers = expressionEditorAtPath(LINE_NUMBERS_PATH);

  return (
    <VStack>
      <Button
        isFullWidth
        onClick={handleOpenCodeEditor}
        suffix={TabNewIcon}
        data-automation-id="open-code-block-editor-button"
      >
        Open Code Editor
      </Button>
      {Language}
      {Theme}
      {LineNumbers}
    </VStack>
  );
};

const CodeBlockType = createIntegratedRootAlias({
  params: ['extraAtomFields'],
  fields: {
    code: withSystem({private: true, isLocalizable: true}, TText),
    language: withMeta(
      {
        variant: 'menu',
      },
      withSystem(
        {
          label: 'Language',
          defaultValue: 'javascript',
          isFullWidth: true,
          isLocalizable: true,
        },
        TEnum(SUPPORTED_LANGUAGES)
      )
    ),
    theme: withMeta(
      {
        variant: 'menu',
      },
      withSystem(
        {
          label: 'Theme',
          defaultValue: 'dark',
          isFullWidth: true,
          isLocalizable: true,
        },
        TEnum(THEMES)
      )
    ),
    lineNumbers: withSystem(
      {
        label: 'Show line numbers',
        labelVariant: 'hidden',
        isLocalizable: true,
      },
      TTypeConstructor(
        qualify(Plugins.name('Basic'), typeName('ReverseLabelBoolean'))
      )
    ),
  },
  render: CodeBlockTypeRender,
});

export default CodeBlockType;
